import { useMemo } from "react";
import { geoJSON, latLng, LatLngBounds } from "leaflet";
import { GeoJsonObject } from "geojson";
import wkx from "wkx";
import union from "@turf/union";
import { Feature, feature, MultiPolygon, Polygon } from "@turf/helpers";

import { useRegions, useRegularGrid } from ".";
import { GetRegionsBoundsLengthError } from "src/errors";
import { Region, RegularGrid } from "src/types";

export function useBounds(caseId: number | string): LatLngBounds {
  const { data: regions } = useRegions();
  const { data: regularGrid } = useRegularGrid(caseId);

  const bounds = useMemo(() => {
    if (regularGrid) {
      return getRegularGridBounds(regularGrid);
    } else if (regions) {
      return getRegionsBounds(regions);
    } else {
      return initialLatLngBounds();
    }
  }, [regions, regularGrid]);

  return bounds;
}

export function useBoundsWithoutCase(): LatLngBounds {
  const { data: regions } = useRegions();

  const bounds = useMemo(() => {
    if (regions) {
      return getRegionsBounds(regions);
    } else {
      return initialLatLngBounds();
    }
  }, [regions]);

  return bounds;
}

function getRegularGridBounds(regularGrid: RegularGrid): LatLngBounds {
  const geometry = wkx.Geometry.parse(regularGrid.polygon_frontendSrid).toGeoJSON() as GeoJsonObject;
  const bounds = geoJSON(geometry).getBounds();
  return bounds;
}

function getRegionsBounds(regions: Region[]): LatLngBounds {
  function regionToFeature(region: Region) {
    const { geom_frontendSrid } = region;
    const regionGeometry = wkx.Geometry.parse(geom_frontendSrid).toGeoJSON();
    const regionFeature = feature(regionGeometry) as Feature<Polygon | MultiPolygon>;
    return regionFeature;
  }

  if (regions.length === 1) {
    const regionFeature = regionToFeature(regions[0]);
    return geoJSON(regionFeature).getBounds();
  } else if (regions.length > 1) {
    const regionsFeatureUnion = regions.reduce(
      (featureUnion: Feature<Polygon | MultiPolygon> | null, region: Region) => {
        const regionFeature = regionToFeature(region);
        if (featureUnion === null) return regionFeature;
        else return union(featureUnion, regionFeature);
      },
      null
    );

    if (regionsFeatureUnion !== null) {
      const bounds = geoJSON(regionsFeatureUnion).getBounds();
      return bounds;
    } else {
      return initialLatLngBounds();
    }
  }

  throw GetRegionsBoundsLengthError();
}

function initialLatLngBounds(): LatLngBounds {
  const [lat, lng] = window._env_.REACT_APP_INITIAL_POSITION!.split(",");
  return latLng(parseFloat(lat), parseFloat(lng)).toBounds(500);
}
